/**
 * Self-hosted web fonts a shop theme may use (theme.typography.heading/body).
 *
 * Mirrors the server allowlist in maplexxon-commerce-server/config/fonts.js:
 * add or remove a font in both repos together. Each font is a dynamic import,
 * so its CSS (and, through it, its woff2 files) only downloads when the active
 * theme names it. Nothing is preloaded.
 *
 * `family` is the CSS family name registered by the @fontsource package.
 * `weights` are the weights the font really has; headings snap to the nearest
 * one instead of letting the browser fake a bold.
 */
const ALL_WEIGHTS = [400, 500, 600, 700];

export const FONTS = {
  // Serifs
  fraunces: {
    family: 'Fraunces Variable',
    category: 'serif',
    weights: ALL_WEIGHTS,
    // Optical-size axis: softer, more open letterforms at display sizes.
    load: () => import('@fontsource-variable/fraunces/opsz.css'),
  },
  'cormorant-garamond': {
    family: 'Cormorant Garamond Variable',
    category: 'serif',
    weights: ALL_WEIGHTS,
    load: () => import('@fontsource-variable/cormorant-garamond'),
  },
  'dm-serif-display': {
    family: 'DM Serif Display',
    category: 'serif',
    weights: [400],
    load: () => import('@fontsource/dm-serif-display/400.css'),
  },
  'instrument-serif': {
    family: 'Instrument Serif',
    category: 'serif',
    weights: [400],
    load: () => import('@fontsource/instrument-serif/400.css'),
  },
  'playfair-display': {
    family: 'Playfair Display Variable',
    category: 'serif',
    weights: ALL_WEIGHTS,
    load: () => import('@fontsource-variable/playfair-display'),
  },
  'bodoni-moda': {
    family: 'Bodoni Moda Variable',
    category: 'serif',
    weights: ALL_WEIGHTS,
    load: () => import('@fontsource-variable/bodoni-moda'),
  },
  // Sans
  'plus-jakarta-sans': {
    family: 'Plus Jakarta Sans Variable',
    category: 'sans',
    weights: ALL_WEIGHTS,
    load: () => import('@fontsource-variable/plus-jakarta-sans'),
  },
  'dm-sans': {
    family: 'DM Sans Variable',
    category: 'sans',
    weights: ALL_WEIGHTS,
    load: () => import('@fontsource-variable/dm-sans'),
  },
  manrope: {
    family: 'Manrope Variable',
    category: 'sans',
    weights: ALL_WEIGHTS,
    load: () => import('@fontsource-variable/manrope'),
  },
  'work-sans': {
    family: 'Work Sans Variable',
    category: 'sans',
    weights: ALL_WEIGHTS,
    load: () => import('@fontsource-variable/work-sans'),
  },
  outfit: {
    family: 'Outfit Variable',
    category: 'sans',
    weights: ALL_WEIGHTS,
    load: () => import('@fontsource-variable/outfit'),
  },
  figtree: {
    family: 'Figtree Variable',
    category: 'sans',
    weights: ALL_WEIGHTS,
    load: () => import('@fontsource-variable/figtree'),
  },
  'space-grotesk': {
    family: 'Space Grotesk Variable',
    category: 'sans',
    weights: ALL_WEIGHTS,
    load: () => import('@fontsource-variable/space-grotesk'),
  },
  archivo: {
    family: 'Archivo Variable',
    category: 'sans',
    weights: ALL_WEIGHTS,
    load: () => import('@fontsource-variable/archivo'),
  },
};

export const FONT_IDS = Object.keys(FONTS);

const FALLBACK_STACKS = {
  serif: 'ui-serif, Georgia, Cambria, "Times New Roman", serif',
  sans: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};

export function isFontId(id) {
  return typeof id === 'string' && Object.prototype.hasOwnProperty.call(FONTS, id);
}

/** CSS font-family value for a FontId, with a system fallback of the same kind. */
export function fontStack(id) {
  if (!isFontId(id)) return FALLBACK_STACKS.sans;
  const font = FONTS[id];
  return `"${font.family}", ${FALLBACK_STACKS[font.category]}`;
}

/** The available weight of `id` closest to `weight` (ties go to the heavier one). */
export function nearestWeight(id, weight) {
  const weights = isFontId(id) ? FONTS[id].weights : ALL_WEIGHTS;
  return weights.reduce((best, w) =>
    Math.abs(w - weight) < Math.abs(best - weight) ||
    (Math.abs(w - weight) === Math.abs(best - weight) && w > best)
      ? w
      : best
  );
}

const requested = new Set();

/** Start downloading the given fonts (once each). Unknown ids are ignored. */
export function loadFonts(ids) {
  const jobs = [...new Set(ids)].filter(isFontId).filter((id) => !requested.has(id));
  return Promise.all(
    jobs.map((id) => {
      requested.add(id);
      return FONTS[id].load().catch(() => requested.delete(id));
    })
  );
}
